import type {
  BdIntervalRelativePart,
} from './relative-date-interval';
import {
  BdIntervalRelative
} from './relative-date-interval';
import { BdDateInterval } from './date-interval';

export abstract class BdTimeSelection {
  public static fromObject(
    obj:
      | {
          type: 'calendric';
          interval: unknown;
        }
      | {
          type: 'relative';
          intervalRelative: {
            start: BdIntervalRelativePart;
            duration: BdIntervalRelativePart;
          };
        },
  ): BdTimeSelection {
    if (!obj) {
      throw new Error('Missing input object.');
    }

    let timeSelection: BdTimeSelection;

    switch (obj.type) {
      case 'relative':
        timeSelection = BdTimeSelectionRelative.fromObject(obj);
        break;
      case 'calendric':
        timeSelection = BdTimeSelectionCalendric.fromObject(obj);
        break;
    }

    return timeSelection;
  }

  public readonly type: 'relative' | 'calendric';

  public abstract clone(): BdTimeSelection;

  public abstract equals(toCompare: BdTimeSelection): boolean;

  public abstract toJSON():
    | {
        type: 'relative';
        intervalRelative: {
          start: BdIntervalRelativePart;
          duration: BdIntervalRelativePart;
        };
      }
    | {
        type: 'calendric';
        interval: {
          start: Date;
          end: Date;
        };
      };

  public abstract valid(): boolean;
}

export class BdTimeSelectionRelative extends BdTimeSelection {
  public static override fromObject(obj: {
    type: 'relative';
    intervalRelative: {
      start: BdIntervalRelativePart;
      duration: BdIntervalRelativePart;
    };
  }): BdTimeSelectionRelative {
    const intervalRelative = new BdIntervalRelative(
      obj.intervalRelative.start,
      obj.intervalRelative.duration,
    );

    return new BdTimeSelectionRelative(intervalRelative);
  }

  override type: 'relative' | 'calendric' = 'relative';

  constructor(public intervalRelative: BdIntervalRelative) {
    super();
  }

  equals(toCompare: BdTimeSelectionRelative): boolean {
    return (
      this.type === toCompare.type &&
      BdIntervalRelative.equals(
        this.intervalRelative,
        toCompare.intervalRelative,
      )
    );
  }

  clone(): BdTimeSelectionRelative {
    return new BdTimeSelectionRelative(
      new BdIntervalRelative(
        this.intervalRelative.start,
        this.intervalRelative.duration,
      ),
    );
  }

  override valid(): boolean {
    return !!this.type && !!this.intervalRelative;
  }

  override toJSON(): {
    type: 'relative';
    intervalRelative: {
      start: BdIntervalRelativePart;
      duration: BdIntervalRelativePart;
    };
  } {
    return {
      type: 'relative',
      intervalRelative: this.intervalRelative.toJSON(),
    };
  }
}

export class BdTimeSelectionCalendric extends BdTimeSelection {
  public static override fromObject(obj: {
    type: 'calendric';
    interval: unknown;
  }): BdTimeSelectionCalendric {
    const interval = BdDateInterval.fromObject(obj.interval);

    return new BdTimeSelectionCalendric(interval);
  }

  override type: 'relative' | 'calendric' = 'calendric';

  constructor(public interval: BdDateInterval) {
    super();
  }

  clone(): BdTimeSelectionCalendric {
    return new BdTimeSelectionCalendric(this.interval.clone());
  }

  equals(toCompare: BdTimeSelectionCalendric): boolean {
    return (
      this.type === toCompare.type &&
      BdDateInterval.equals(this.interval, toCompare.interval)
    );
  }

  override valid(): boolean {
    return !!this.type && !!this.interval && this.interval.isValid();
  }

  override toJSON(): {
    type: 'calendric';
    interval: {
      start: Date;
      end: Date;
    };
  } {
    return {
      type: 'calendric',
      interval: {
        start: this.interval.start,
        end: this.interval.end,
      },
    };
  }
}
